/**
 * Max session duration - an absolute cap on how long a signed-in session lasts,
 * regardless of activity. Off by default; see config.js's `session` block.
 *
 *		import { useMaxSessionDuration } from "/helpers/useMaxSessionDuration.js";
 *
 *		useMaxSessionDuration({ onTimeout: () => signOut(SIGN_OUT_REASON.MaxDuration) });
 *
 * This is deliberately separate from the idle timeout (/helpers/useIdleTimeout.js):
 * idle tracks quiet time and warns before signing out; this tracks wall-clock time
 * since login and signs out immediately once it's reached - no warning, since the
 * point is a hard ceiling rather than a "still there?" check.
 *
 * The session's start time lives on globalStore (persisted through the same
 * localStorage record as the rest of the signed-in user), set once at login and
 * carried as-is through page reloads - see globalStore.js::setCurrentUser().
 */

import { onMounted, onBeforeUnmount } from "vue";
import config from "/config.js";
import globalStore from "/globalStore.js";


// How often to check. Doesn't need to be precise to the second - the cap is
// measured in hours, not seconds.
const CHECK_INTERVAL_MS = 30 * 1000;




//-- Methods

/**
 * Watch wall-clock time since login and fire once the configured cap is passed.
 * A no-op (no timer started) when `config.session.maxDurationEnabled` is false.
 *
 * @param {function} options.onTimeout Called once when the cap is reached.
 */
export function useMaxSessionDuration(options = {})
{
	const onTimeout = options.onTimeout ? options.onTimeout : () => { };

	let checkTimer = null;
	let hasTimedOut = false;


	const check = () =>
	{
		if (config.session.maxDurationEnabled != true) return;
		if (hasTimedOut) return;

		const sessionStartedAtUtc = globalStore.state.currentUser.sessionStartedAtUtc;
		if (sessionStartedAtUtc == null) return;	// No recorded start (e.g. a session from before this feature) - nothing to enforce yet.

		const elapsedMinutes = (Date.now() - sessionStartedAtUtc) / 60000;
		if (elapsedMinutes < config.session.maxDurationMinutes) return;

		hasTimedOut = true;
		stop();
		onTimeout();
	};


	const start = () =>
	{
		if (config.session.maxDurationEnabled != true) return;
		if (checkTimer != null) return;

		checkTimer = setInterval(check, CHECK_INTERVAL_MS);
	};


	const stop = () =>
	{
		if (checkTimer == null) return;

		clearInterval(checkTimer);
		checkTimer = null;
	};




	//-- Lifecycle Hooks

	onMounted(() =>
	{
		check();	// Catch a cap already passed (e.g. a long-open tab) as soon as this mounts.
		start();
	});


	onBeforeUnmount(() =>
	{
		stop();
	});


	return { stop };
}


export default { useMaxSessionDuration };
